import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { BrandMark } from "../components/BrandMark";
import { PublicMobileMenu } from "../components/PublicMobileMenu";
import { OfficeHours, OFFICE_HOURS_LINES } from "../lib/office-hours";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import {
    Menu, X, ArrowRight, Phone, Mail, MapPin, Clock, Info, MapPinned,
    Facebook, Instagram, Linkedin, MessageCircle,
} from "lucide-react";

/* ---------- nav ---------- */
const NAV = [
    { label: "Home", to: "/home" },
    { label: "About Us", to: "/about-us" },
    { label: "Loans", to: "/loans" },
    { label: "Saving Scheme", to: "/daily-savings" },
    { label: "Investment", to: "/investment" },
    { label: "Careers", to: "/careers" },
    { label: "Contact Us", to: "/contact-us" },
];

/* real contact details */
const PHONE = "+91 93592 86008";
const PHONE_TEL = "+919359286008";
const SUPPORT_EMAIL = "support@lokdhara.co";
const ADDRESS = "Sai Sanskar Apartment, Kachare Galli, Dange Chauk Road, Ishwarpur, 415409";
const MAPS_LINK = "https://maps.app.goo.gl/98yYfGcodyHcUgk78";

const FOOTER_LINKS = {
    Products: ["Personal Loan", "Business Loan", "Group Loan", "Saving Scheme", "Investment"],
    Company: ["About Us", "Careers", "News & Updates", "Contact Us"],
    Support: ["FAQs", "Grievance Redressal", "Customer Care", "Downloads"],
};

const placeholderLoginToast = () => {
    window.location.assign("/customer-login");
};

/* ---------- page ---------- */
export default function PublicContact() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        document.title = "Contact Us | Lokdhara Microfinance";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    const go = (to) => { if (to) navigate(to); };

    return (
        <div id="top" className="min-h-screen bg-white text-slate-900 antialiased">
            {/* HEADER ---------------------------------------------------- */}
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
                    <Link to="/home" className="flex items-center" data-testid="con-brand">
                        <BrandMark size={56} className="sm:!h-14" />
                    </Link>
                    <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-slate-700">
                        {NAV.map((n) => (
                            <button key={n.label}
                                onClick={() => go(n.to)}
                                className={`hover:text-brand-royal transition ${n.to === "/contact-us" ? "text-brand-royal" : ""}`}
                                data-testid={`con-nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        <Button
                            className="hidden sm:inline-flex bg-brand-royal hover:bg-brand-hover text-white rounded-full h-10 px-5"
                            onClick={placeholderLoginToast}
                            data-testid="con-header-customer-login"
                        >
                            Customer Login
                        </Button>
                        <button
                            aria-label="Open menu"
                            className="lg:hidden h-10 w-10 rounded-lg border border-slate-200 grid place-items-center"
                            onClick={() => setMenuOpen(true)}
                            data-testid="con-mobile-menu-btn"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                <PublicMobileMenu
                    open={menuOpen}
                    onOpenChange={setMenuOpen}
                    nav={NAV}
                    onNavItemClick={(n) => go(n.to)}
                    onLoginClick={placeholderLoginToast}
                    testId="con-mobile-menu"
                    activePath="/contact-us"
                />
            </header>

            {/* NAVY HERO ------------------------------------------------ */}
            <section className="relative overflow-hidden bg-brand-navy text-white">
                <div className="absolute inset-0 pointer-events-none opacity-20"
                    style={{ backgroundImage: "radial-gradient(circle at 15% 25%, rgba(255,255,255,0.15) 0%, transparent 45%), radial-gradient(circle at 85% 65%, rgba(255,255,255,0.10) 0%, transparent 40%)" }}
                    aria-hidden
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-tint bg-white/10 rounded-full px-3 py-1">
                        We&apos;re here to help
                    </span>
                    <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight max-w-3xl">
                        Contact Lokdhara Microfinance
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-slate-200/90 max-w-2xl leading-relaxed">
                        For product information, account assistance, careers or general enquiries —
                        reach us through the channels below.
                    </p>
                </div>
            </section>

            {/* NOTE 1 --------------------------------------------------- */}
            <section className="pt-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <Card className="rounded-2xl border-brand-soft bg-brand-soft/50 p-6 sm:p-7 flex items-start gap-4"
                        data-testid="con-note-top"
                    >
                        <div className="h-10 w-10 rounded-xl bg-white grid place-items-center shrink-0">
                            <Info className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                        </div>
                        <p className="text-slate-700 leading-relaxed">
                            You may visit our branch, call us, or email us for product information,
                            account assistance, careers and general enquiries.
                        </p>
                    </Card>
                </div>
            </section>

            {/* CONTACT CARDS ------------------------------------------- */}
            <section className="py-16 sm:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                        <Card className="rounded-2xl border-slate-100 p-6 hover:shadow-card-hover transition flex flex-col"
                            data-testid="con-card-phone"
                        >
                            <div className="h-12 w-12 rounded-xl bg-brand-soft grid place-items-center">
                                <Phone className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                            </div>
                            <div className="mt-4 text-xs uppercase tracking-wider font-semibold text-slate-500">Phone</div>
                            <div className="mt-1 text-slate-800 font-semibold">{PHONE}</div>
                            <a
                                href={`tel:${PHONE_TEL}`}
                                className="mt-5"
                                data-testid="con-tel-link"
                            >
                                <Button className="w-full rounded-full h-11 bg-brand-navy hover:bg-brand-royal text-white">
                                    Call Us <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </a>
                        </Card>

                        <Card className="rounded-2xl border-slate-100 p-6 hover:shadow-card-hover transition flex flex-col"
                            data-testid="con-card-email"
                        >
                            <div className="h-12 w-12 rounded-xl bg-brand-soft grid place-items-center">
                                <Mail className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                            </div>
                            <div className="mt-4 text-xs uppercase tracking-wider font-semibold text-slate-500">Email</div>
                            <div className="mt-1 text-slate-800 font-semibold break-all">{SUPPORT_EMAIL}</div>
                            <a
                                href={`mailto:${SUPPORT_EMAIL}?subject=Enquiry`}
                                className="mt-5"
                                data-testid="con-mail-link"
                            >
                                <Button className="w-full rounded-full h-11 bg-brand-navy hover:bg-brand-royal text-white">
                                    Email Us <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </a>
                        </Card>

                        <Card className="rounded-2xl border-slate-100 p-6 hover:shadow-card-hover transition flex flex-col md:col-span-2 lg:col-span-1"
                            data-testid="con-card-address"
                        >
                            <div className="h-12 w-12 rounded-xl bg-brand-soft grid place-items-center">
                                <MapPin className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                            </div>
                            <div className="mt-4 text-xs uppercase tracking-wider font-semibold text-slate-500">Address</div>
                            <div className="mt-1 text-slate-800 font-semibold leading-snug">{ADDRESS}</div>
                        </Card>

                        <Card className="rounded-2xl border-slate-100 p-6 hover:shadow-card-hover transition flex flex-col md:col-span-2 lg:col-span-1"
                            data-testid="con-card-hours"
                        >
                            <div className="h-12 w-12 rounded-xl bg-brand-soft grid place-items-center">
                                <Clock className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                            </div>
                            <div className="mt-4 text-xs uppercase tracking-wider font-semibold text-slate-500">Office Hours</div>
                            <OfficeHours
                                className="mt-1 space-y-0.5 text-slate-800 font-semibold text-sm leading-snug"
                            />
                        </Card>
                    </div>
                </div>
            </section>

            {/* ENQUIRY FORM (visual only) ----------------------------- */}
            <section className="bg-slate-50 py-20 sm:py-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-start">
                    <div>
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Enquiry Form
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                            Send us a message
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            Share a few details and our team will get back to you.
                        </p>
                        <ul className="mt-6 space-y-3 text-sm text-slate-700">
                            <li className="flex items-start gap-2">
                                <Phone className="h-4 w-4 text-brand-royal mt-0.5 shrink-0" />
                                <span>{PHONE}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Mail className="h-4 w-4 text-brand-royal mt-0.5 shrink-0" />
                                <span>{SUPPORT_EMAIL}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <MapPin className="h-4 w-4 text-brand-royal mt-0.5 shrink-0" />
                                <span>{ADDRESS}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Clock className="h-4 w-4 text-brand-royal mt-0.5 shrink-0" />
                                <div className="space-y-0.5">
                                    {OFFICE_HOURS_LINES.map((l) => (<div key={l}>{l}</div>))}
                                </div>
                            </li>
                        </ul>
                    </div>

                    <Card className="rounded-3xl border-slate-100 shadow-card p-6 sm:p-8"
                        data-testid="con-enquiry-card"
                    >
                        <form
                            className="space-y-4"
                            onSubmit={async (e) => {
                                e.preventDefault();
                                const form = e.currentTarget;
                                const submitBtn = form.querySelector('[data-testid="con-form-submit"]');
                                const fd = new FormData(form);
                                const payload = {
                                    name: (fd.get("name") || "").toString().trim(),
                                    mobile: (fd.get("mobile") || "").toString().trim(),
                                    email: (fd.get("email") || "").toString().trim(),
                                    subject: (fd.get("subject") || "").toString().trim(),
                                    message: (fd.get("message") || "").toString().trim(),
                                };
                                if (submitBtn) submitBtn.disabled = true;
                                try {
                                    await api.post("/enquiries", payload);
                                    toast.success("Thanks! Our team will be in touch shortly.");
                                    form.reset();
                                } catch (err) {
                                    toast.error(formatApiError(err));
                                } finally {
                                    if (submitBtn) submitBtn.disabled = false;
                                }
                            }}
                            data-testid="con-enquiry-form"
                        >
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        Full Name
                                    </label>
                                    <Input
                                        name="name"
                                        placeholder="Enter your name"
                                        className="mt-2 h-11 rounded-xl"
                                        required
                                        data-testid="con-form-name"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        Mobile Number
                                    </label>
                                    <Input
                                        name="mobile"
                                        placeholder="+91 XXXXXXXXXX"
                                        inputMode="tel"
                                        className="mt-2 h-11 rounded-xl"
                                        required
                                        data-testid="con-form-mobile"
                                    />
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        Email
                                    </label>
                                    <Input
                                        name="email"
                                        placeholder="you@example.com"
                                        type="email"
                                        className="mt-2 h-11 rounded-xl"
                                        data-testid="con-form-email"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        Subject
                                    </label>
                                    <Input
                                        name="subject"
                                        placeholder="How can we help?"
                                        className="mt-2 h-11 rounded-xl"
                                        data-testid="con-form-subject"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Message
                                </label>
                                <Textarea
                                    name="message"
                                    placeholder="Write your message"
                                    rows={5}
                                    className="mt-2 rounded-xl"
                                    required
                                    data-testid="con-form-message"
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full sm:w-auto rounded-full h-12 px-8 bg-brand-royal hover:bg-brand-hover text-white"
                                data-testid="con-form-submit"
                            >
                                Send Message <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>

                            <p className="text-[11px] text-slate-500 leading-relaxed">
                                By submitting, you agree that our team may contact you regarding your
                                enquiry.
                            </p>
                        </form>
                    </Card>
                </div>
            </section>

            {/* MAP (embedded Google Map) ----------------------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Locate Us
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            Our branch on the map
                        </h2>
                        <p className="mt-3 text-sm text-slate-600">{ADDRESS}</p>
                    </div>
                    <div
                        className="relative rounded-3xl border border-slate-100 shadow-card overflow-hidden bg-slate-100"
                        data-testid="con-map-embed"
                    >
                        <iframe
                            title="Lokdhara Microfinance branch location"
                            src="https://www.google.com/maps?q=17.0500787,74.2613644&hl=en&z=17&output=embed"
                            className="w-full h-[360px] sm:h-[440px] border-0"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                        />
                    </div>
                    <div className="mt-6 flex justify-center">
                        <a
                            href={MAPS_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="con-map-directions"
                        >
                            <Button className="rounded-full h-12 px-6 bg-brand-navy hover:bg-brand-royal text-white">
                                <MapPinned className="h-4 w-4 mr-2" /> Get Directions
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* NOTE 2 -------------------------------------------------- */}
            <section className="pb-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <Card className="rounded-2xl border-brand-soft bg-brand-soft/50 p-6 sm:p-7 flex items-start gap-4"
                        data-testid="con-note-bottom"
                    >
                        <div className="h-10 w-10 rounded-xl bg-white grid place-items-center shrink-0">
                            <Info className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-royal">
                                Please note
                            </div>
                            <p className="mt-2 text-slate-700 leading-relaxed">
                                Our team will contact you after receiving your call, email or enquiry.
                            </p>
                        </div>
                    </Card>
                </div>
            </section>

            {/* CTA ---------------------------------------------------- */}
            <section className="bg-gradient-to-br from-brand-navy to-brand-royal text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        We&apos;re just a call or email away.
                    </h2>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <a href={`tel:${PHONE_TEL}`} data-testid="con-cta-tel">
                            <Button className="bg-white text-brand-navy hover:bg-brand-soft rounded-full h-12 px-6 font-semibold">
                                Call {PHONE}
                            </Button>
                        </a>
                        <a href={`mailto:${SUPPORT_EMAIL}?subject=Enquiry`} data-testid="con-cta-mail">
                            <Button
                                variant="outline"
                                className="rounded-full h-12 px-6 border-white/40 text-white hover:bg-white/10 hover:text-white"
                            >
                                Email {SUPPORT_EMAIL}
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* FOOTER ------------------------------------------------- */}
            <footer className="bg-brand-navy text-slate-300">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8">
                    <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
                        <div className="lg:col-span-2">
                            <BrandMark size={40} variant="white" />
                            <p className="mt-5 text-sm leading-relaxed max-w-md text-slate-400">
                                Lokdhara Microfinance is focused on providing structured and accessible
                                financial services through transparent processes, clear records and
                                customer-oriented support.
                            </p>
                            <div className="mt-5 flex gap-3">
                                {[Facebook, Instagram, Linkedin, MessageCircle].map((Icon, i) => (
                                    <a key={i} href="/home"
                                        className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 grid place-items-center transition"
                                        aria-label={`social-${i}`}
                                    >
                                        <Icon className="h-4 w-4" />
                                    </a>
                                ))}
                            </div>
                        </div>
                        {Object.entries(FOOTER_LINKS).map(([col, items]) => (
                            <div key={col}>
                                <div className="text-white font-semibold text-sm mb-4">{col}</div>
                                <ul className="space-y-2 text-sm">
                                    {items.map((item) => (
                                        <li key={item}>
                                            <button
                                                onClick={() => {
                                                    if (item === "About Us") navigate("/about-us");
                                                    else if (["Personal Loan", "Business Loan", "Group Loan"].includes(item)) navigate("/loans");
                                                    else if (item === "Saving Scheme") navigate("/daily-savings");
                                                    else if (item === "Investment") navigate("/investment");
                                                    else if (item === "Careers") navigate("/careers");
                                                    else if (item === "Contact Us") navigate("/contact-us");
                                                    else navigate("/home");
                                                }}
                                                className="hover:text-white transition text-left"
                                            >
                                                {item}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <p className="mt-12 text-xs text-slate-400 leading-relaxed max-w-3xl">
                        Product availability, eligibility, rates and terms are subject to approval and
                        applicable requirements.
                    </p>

                    <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
                        <div>© {new Date().getFullYear()} Lokdhara Microfinance. All rights reserved.</div>
                        <div className="flex items-center gap-5">
                            <button onClick={() => navigate("/home")} className="hover:text-white">Privacy Policy</button>
                            <button onClick={() => navigate("/home")} className="hover:text-white">Terms &amp; Conditions</button>
                            <Link
                                to="/login"
                                className="text-slate-300 hover:text-white underline underline-offset-4"
                                data-testid="con-footer-staff-login"
                            >
                                Staff Login
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
